import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";

const GraftonScreenOrb = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    const size = 200;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);
    const cx = size / 2, cy = size / 2;
    let t = 0;

    const draw = () => {
      t += 0.02;
      ctx.clearRect(0, 0, size, size);
      const glow = "210, 170, 100";
      const glowR = 60 + Math.sin(t * 0.5) * 8;
      const grad = ctx.createRadialGradient(cx, cy, 10, cx, cy, glowR + 30);
      grad.addColorStop(0, `rgba(${glow}, 0.2)`);
      grad.addColorStop(0.5, `rgba(${glow}, 0.06)`);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);

      for (let r = 0; r < 4; r++) {
        const baseR = 25 + r * 12;
        const alpha = 0.35 - r * 0.07;
        ctx.beginPath();
        for (let a = 0; a <= Math.PI * 2; a += 0.02) {
          const wave = Math.sin(a * 3 + t + r * 1.2) * 14 * (1 - r * 0.15);
          const radius = baseR + wave;
          const x = cx + Math.cos(a) * radius;
          const y = cy + Math.sin(a) * radius;
          a === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(${glow}, ${alpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 22);
      coreGrad.addColorStop(0, `rgba(${glow}, 0.45)`);
      coreGrad.addColorStop(0.6, `rgba(${glow}, 0.15)`);
      coreGrad.addColorStop(1, `rgba(${glow}, 0)`);
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, 22, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = `rgba(${glow}, 0.9)`;
      ctx.font = "bold 18px serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("G", cx, cy);

      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return <canvas ref={canvasRef} />;
};

export default function MacBookAnimation() {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setOpened(true), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-8">
      {/* MacBook Pro container */}
      <div className="relative" style={{ perspective: "1200px" }}>
        {/* Screen (lid) */}
        <motion.div
          className="relative mx-auto rounded-t-xl overflow-hidden"
          style={{
            width: 340,
            height: 220,
            transformOrigin: "bottom center",
            background: "linear-gradient(135deg, hsl(222 28% 11%), hsl(222 28% 8%))",
            border: "2px solid hsl(var(--border))",
            borderBottom: "none",
          }}
          initial={{ rotateX: -85 }}
          animate={{ rotateX: opened ? 0 : -85 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        >
          {/* Bezel top */}
          <div className="absolute top-0 left-0 right-0 h-3 bg-[hsl(222,20%,14%)] flex items-center justify-center">
            <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
          </div>

          {/* Screen content */}
          <motion.div
            className="absolute inset-0 top-3 flex flex-col items-center justify-center"
            style={{ background: "radial-gradient(ellipse at center, hsl(222 30% 12%), hsl(222 30% 6%))" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: opened ? 1 : 0 }}
            transition={{ delay: 1.6, duration: 0.8 }}
          >
            <GraftonScreenOrb />
            <motion.p
              className="text-[10px] font-medium tracking-widest uppercase text-primary/80 mt-1"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: opened ? 1 : 0, y: opened ? 0 : 6 }}
              transition={{ delay: 2.2, duration: 0.6 }}
            >
              Mac et Grafton IA
            </motion.p>
          </motion.div>
        </motion.div>

        {/* Base (keyboard deck) */}
        <div
          className="mx-auto rounded-b-lg"
          style={{
            width: 380,
            height: 12,
            background: "linear-gradient(to bottom, hsl(222 20% 18%), hsl(222 20% 14%))",
            border: "2px solid hsl(var(--border))",
            borderTop: "1px solid hsl(var(--border) / 0.5)",
          }}
        />
        {/* Notch / hinge */}
        <div
          className="mx-auto rounded-b-sm"
          style={{
            width: 60,
            height: 3,
            background: "hsl(222 20% 20%)",
            marginTop: -1,
          }}
        />
      </div>

      {/* Caption */}
      <motion.div
        className="mt-6 text-center space-y-1"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: opened ? 1 : 0, y: opened ? 0 : 10 }}
        transition={{ delay: 2.4, duration: 0.6 }}
      >
        <p className="text-sm font-semibold text-foreground">
          <span className="text-primary">Mac et Grafton IA</span> — l'alliance ultime
        </p>
        <p className="text-xs text-muted-foreground">
          Votre MacBook Pro devient un cerveau stratégique
        </p>
      </motion.div>
    </div>
  );
}
